<template>
  <el-dialog
      :title="`明细拆分 - ${detailData ? detailData.materialName : ''}`"
      :visible.sync="visible"
      width="800px"
      :close-on-click-modal="false"
      @close="handleClose"
  >
    <el-form ref="splitForm" :model="splitForm" label-width="100px">
      <el-form-item label="原数量">
        <el-input v-model="detailData.quantity" disabled/>
      </el-form-item>
      <el-form-item label="原单价">
        <el-input v-model="detailData.unitPrice" disabled/>
      </el-form-item>
    </el-form>
    <div style="margin: 10px 0; text-align: left">
      <el-button type="primary" size="small" @click="addSplitItem">添加行</el-button>
    </div>
    <el-table
        :data="splitList"
        style="width: 100%"
        border
    >
      <el-table-column type="index" label="序号" width="80"/>
      <el-table-column prop="quantity" label="数量" width="180">
        <template slot-scope="scope">
          <el-input-number
              v-model="scope.row.quantity"
              :precision="1"
              @change="handleQuantityChange(scope.row)"
          />
        </template>
      </el-table-column>
      <el-table-column prop="unitPrice" label="单价" width="180">
        <template slot-scope="scope">
          <el-input
              v-model="scope.row.unitPrice"
              type="number"
              @change="handleUnitPriceChange(scope.row)"
          />
        </template>
      </el-table-column>
      <el-table-column prop="totalPrice" label="总价(含税)">
        <template slot-scope="scope">
          <el-input 
              v-model="scope.row.totalPrice" 
              type="number"
              @change="handleTotalPriceChange(scope.row)"
          />
        </template>
      </el-table-column>
      <el-table-column label="操作" width="100">
        <template slot-scope="scope">
          <el-button
              type="danger"
              size="small"
              :disabled="splitList.length <= 1"
              @click="removeSplitItem(scope.$index)"
          >
            删除
          </el-button>
        </template>
      </el-table-column>
    </el-table>
   
    <div style="margin-top: 20px; text-align: right">
      <span style="margin-right: 20px">
        拆分总金额: {{parseFloat(splitTotalPrice).toFixed(2)}} / {{detailData ? parseFloat(detailData.totalPrice).toFixed(2) : 0}}
      </span>
      <el-button @click="handleClose">取消</el-button>
      <el-button type="primary" :disabled="!canConfirmSplit" @click="confirmSplit">确定</el-button>
    </div>
  </el-dialog>
</template>

<script>
  export default {
    name: "DetailSplitDialog",
    props: {
      visible: {
        type: Boolean,
        default: false,
      },
      detailData: {
        type: Object,
        required: true,
      },
      detailType: {
        type: String,
        required: true,
        default: "delivery",
        validator: (value) => ["delivery", "sale"].includes(value),
      },
    },
    data() {
      return {
        splitForm: {},
        splitList: [],
        splitTotalPrice: 0,
      };
    },
    computed: {
      canConfirmSplit() {
        // 检查拆分总金额是否等于原金额
        const isTotalMatch = Math.abs(this.splitTotalPrice - this.detailData.totalPrice) <= 0.01;
        return isTotalMatch;
      },
    },
    watch: {
      visible(newVal) {
        if (newVal) {
          this.initSplitList();
        }
      },
    },
    methods: {
      initSplitList() {
        // 初始化拆分列表
        this.splitList = [{
          quantity: this.detailData.quantity,
          unitPrice: this.detailData.unitPrice,
          totalPrice: parseFloat((this.detailData.quantity * this.detailData.unitPrice).toFixed(2)),
        }];
        this.updateSplitTotal();
      },
      addSplitItem() {
        this.splitList.push({
          quantity: 1,
          unitPrice: this.detailData.unitPrice,
          totalPrice: parseFloat((1 * this.detailData.unitPrice).toFixed(2)),
        });
        this.updateSplitTotal();
      },
      removeSplitItem(index) {
        this.splitList.splice(index, 1);
        this.updateSplitTotal();
      },
      // 数量改变时重新计算总价
      handleQuantityChange(row) {
        // 根据数量和单价计算总价
        row.totalPrice = (Number(row.quantity) * Number(row.unitPrice)).toFixed(2);
        
        // 更新拆分总金额和总数量
        this.updateSplitTotal();
      },
      // 单价改变时重新计算总价
      handleUnitPriceChange(row) {
        // 根据单价和数量计算总价
        row.totalPrice = (Number(row.quantity) * Number(row.unitPrice)).toFixed(2);
        
        // 更新拆分总金额
        this.updateSplitTotal();
      },
      // 总价改变时反推单价
      handleTotalPriceChange(row) {
        // 确保数量不为0，避免除以0错误
        if (Number(row.quantity) === 0) {
          this.$emit("error", "数量不能为0");
          return;
        }
        
        // 根据总价和数量计算单价
        row.unitPrice = (Number(row.totalPrice) / Number(row.quantity)).toFixed(2);
        
        // 更新拆分总金额
        this.updateSplitTotal();
      },
      updateSplitTotal() {
        // 计算拆分总金额
        this.splitTotalPrice = this.splitList.reduce((sum, item) => sum + Number(item.totalPrice), 0);
      },
      confirmSplit() {
        // 验证拆分总金额是否等于原金额
        const originalTotalPrice = Number(this.detailData.totalPrice);
        // 考虑浮点数精度问题，允许0.01的误差
        if (Math.abs(this.splitTotalPrice - originalTotalPrice) > 0.01) {
          this.$emit("error", "拆分金额总和必须等于原金额");
          return;
        }
      
        // 处理拆分明细
        const splitDetails = this.splitList.map((splitItem, index) => {
          // 复制原数据
          let newItem = JSON.parse(JSON.stringify(this.detailData));
          // 更新拆分后的数据
          newItem.quantity = splitItem.quantity;
          newItem.unitPrice = splitItem.unitPrice;
          newItem.totalPrice = splitItem.totalPrice;
          newItem.totalNetPrice = (Number(splitItem.totalPrice) / (1 + Number(newItem.taxRate) / 100)).toFixed(2);
          newItem.totalTaxPrice = (Number(splitItem.totalPrice) - Number(newItem.totalNetPrice)).toFixed(2);
        
          // 添加标识，用于后续还原
          newItem.isSplit = true;
          newItem.splitParentId = this.detailType === "delivery" ? newItem.deliveryDetailId : newItem.saleOrderDetailId;
          newItem.splitIndex = index;
          newItem.originalData = this.detailData;
        
          return newItem;
        });
      
        // 触发确认事件，将拆分数据传递给父组件
        this.$emit("confirm", splitDetails);
        // 关闭弹窗
        this.handleClose();
      },
      handleClose() {
        // 触发关闭事件
        this.$emit("update:visible", false);
        this.$emit("close");
      },
    },
  };
</script>

<style lang="scss" scoped>
/* 隐藏数字输入框的上下箭头 */
::v-deep {
  input[type="number"]::-webkit-inner-spin-button,
  input[type="number"]::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  
  input[type="number"] {
    appearance: textfield;
  }
}
</style>